import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { Calendar, MapPin, Users, ArrowRight, LogIn, UserPlus } from 'lucide-react';

const EventLandingPage = () => {
  const { event, loading } = useEvent();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return null; // EventContext redirige déjà vers la homepage
  }

  const handleLogin = () => {
    navigate(`/event/${event.slug}/login`);
  };

  const handleRegister = () => {
    navigate(`/event/${event.slug}/register`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <a href="/" className="text-sm text-gray-600 hover:text-gray-900">
              ← Back to home
            </a>
            <div className="text-sm text-gray-600">
              by <span className="font-semibold">Mobivent</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Event Logo */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Left side - Event Info */}
            <div className="p-12 flex flex-col justify-center">
              {/* Event Logo */}
              {event.logo && (
                <div className="mb-8">
                  <img 
                    src={event.logo} 
                    alt={event.name}
                    className="max-h-24 object-contain"
                  />
                </div>
              )}

              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {event.name}
              </h1>

              {event.description && (
                <p className="text-xl text-gray-600 mb-8">
                  {event.description}
                </p>
              )}

              {/* Event Details */}
              <div className="space-y-4 mb-8">
                {event.start_date && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <Calendar className="text-blue-600" size={24} />
                    <div>
                      <p className="font-semibold">Date</p>
                      <p>{new Date(event.start_date).toLocaleDateString('en-GB', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                      {event.end_date && event.end_date !== event.start_date && (
                        <> - {new Date(event.end_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}</>
                      )}
                      </p>
                    </div>
                  </div>
                )}

                {event.location && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <MapPin className="text-blue-600" size={24} />
                    <div>
                      <p className="font-semibold">Location</p>
                      <p>{event.location}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="text-blue-600" size={24} />
                  <div>
                    <p className="font-semibold">B2B Matchmaking</p>
                    <p>Connect with the right partners</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleLogin}
                  className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-lg"
                >
                  <LogIn size={20} />
                  Sign In
                </button>

                <button
                  onClick={handleRegister}
                  className="w-full bg-white text-blue-600 px-6 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition border-2 border-blue-600 flex items-center justify-center gap-2"
                >
                  <UserPlus size={20} />
                  Create Account
                </button>
              </div>

              <p className="text-sm text-gray-500 text-center mt-4">
                First time here? Create an account to access the matchmaking platform
              </p>
            </div>

            {/* Right side - Visual */}
            <div 
              className="hidden md:block bg-gradient-to-br from-blue-500 to-purple-600 p-12"
              style={event.primary_color ? { 
                background: `linear-gradient(to bottom right, ${event.primary_color}, ${event.primary_color}dd)` 
              } : {}}
            >
              <div className="h-full flex flex-col justify-center items-center text-white">
                <div className="text-center">
                  <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-8 mx-auto">
                    <Users size={64} />
                  </div>
                  <h2 className="text-3xl font-bold mb-4">Smart Matchmaking</h2>
                  <p className="text-xl opacity-90 mb-8">
                    Our intelligent algorithm connects you with the most relevant business partners
                  </p>
                  <div className="flex items-center justify-center gap-2 text-lg">
                    <span>Get started</span>
                    <ArrowRight size={20} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Users className="text-blue-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">Smart Matching</h3>
            <p className="text-gray-600">
              Connect with partners based on your business needs and offerings
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Calendar className="text-purple-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">Easy Scheduling</h3>
            <p className="text-gray-600">
              Automatic time slot and table assignment for your meetings
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <MapPin className="text-green-600" size={24} />
            </div>
            <h3 className="text-xl font-bold mb-2">On-site Support</h3>
            <p className="text-gray-600">
              Find your meeting location easily with our interactive maps
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-12">
          <h2 className="text-3xl font-bold text-center mb-12">How it works</h2>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-bold mb-2">Create Account</h3>
              <p className="text-sm text-gray-600">
                Sign up with your professional information
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-bold mb-2">Define Profile</h3>
              <p className="text-sm text-gray-600">
                Specify what you offer and what you're looking for
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-bold mb-2">Get Matches</h3>
              <p className="text-sm text-gray-600">
                Receive relevant partner suggestions
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-bold mb-2">Schedule Meetings</h3>
              <p className="text-sm text-gray-600">
                Request and confirm meetings at the event
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm">
            Powered by <span className="font-semibold">Mobivent</span> • B2B Matchmaking Platform
          </p>
        </div>
      </footer>
    </div>
  );
};

export default EventLandingPage;